import { Reference } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  CommunityFeedPostFragmentDoc,
  MediaInput,
  useCommunitiesV2AddPostMutation,
} from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';
import { uploadMediaInputs } from '../lib/mediaInputs';

// A new post belongs on the first page of the main feed variants only. Skip
// cursor-paginated pages (they carry `lastId`/`direction`) and the pinned list
// (`type: pinned`) — a freshly created post is neither paginated nor pinned.
const isFirstPageVariant = (storeFieldName: string): boolean =>
  !storeFieldName.includes('lastId') &&
  !storeFieldName.includes('direction') &&
  !storeFieldName.includes('"type"');

type AddPostInput = { text: string; files: File[]; videoUuids: string[] };

export type AddPostResult =
  | { type: 'success' }
  | { type: 'failure'; reason: string };

const toFailureReason = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unknown error';

export const useAddPost = (communityId: CommunityId, influencerId?: string) => {
  const [addPostMutation] = useCommunitiesV2AddPostMutation();
  const creatorId = resolveInfluencerId(influencerId);

  return useCallback(
    async ({
      text,
      files,
      videoUuids,
    }: AddPostInput): Promise<AddPostResult> => {
      try {
        let mediaInput: MediaInput[] = [];
        if (files.length > 0 || videoUuids.length > 0) {
          if (creatorId === null) throw new Error('Missing creator id');
          mediaInput = await uploadMediaInputs(files, videoUuids, creatorId);
        }

        await addPostMutation({
          variables: { communityId, text, media: mediaInput },
          update: (cache, { data }) => {
            const created = data?.addPost;
            if (!created) return;

            const ref = cache.writeFragment({
              fragment: CommunityFeedPostFragmentDoc,
              fragmentName: 'CommunityFeedPost',
              data: created,
            });
            if (!ref) return;

            cache.modify({
              id: cache.identify({ __typename: 'Community', id: communityId }),
              fields: {
                posts: (
                  existing: readonly Reference[] = [],
                  { storeFieldName, readField },
                ) => {
                  if (!isFirstPageVariant(storeFieldName)) return existing;
                  const present = existing.some(
                    (postRef) => readField('id', postRef) === created.id,
                  );
                  return present ? existing : [ref, ...existing];
                },
              },
            });
          },
        });
        return { type: 'success' };
      } catch (error) {
        toast.error('Something went wrong');
        return { type: 'failure', reason: toFailureReason(error) };
      }
    },
    [addPostMutation, communityId, creatorId],
  );
};
